/**
 * ContactNotesTable
 * -----------------------------------------------------------------------------
 * Tabela simples de anotações de um contato, com linha clicável para abrir o
 * editor. Botões de excluir só ficam habilitados para autor ou admin.
 *
 * @version 1.0.2
 * RELEASE: 2026-06-03 v1.0.2 - Coluna "Lida" (CheckCheck) + campos
 *                              read_count / is_read_by_me / last_read_at.
 * RELEASE: 2026-06-03 v1.0.1 - Padronizado visual com ScheduledMessagesTable
 *                              (wrapper border rounded-md) e adicionado avatar
 *                              do autor via SmartAvatar (created_by_avatar).
 * RELEASE: 2026-06-03 v1.0.0 - Criação inicial.
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCheck, Loader2, Paperclip, Trash2 } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';

export interface ContactNoteRow {
  id: string;
  contact_id: string;
  content_html: string;
  content_plain: string;
  created_by: string | null;
  created_by_name: string | null;
  /** v1.0.1 — avatar do usuário criador (para SmartAvatar). */
  created_by_avatar?: string | null;
  attachments_count: number;
  /** v1.0.2 — contagem total de usuários que marcaram como lida. */
  read_count?: number;
  /** v1.0.2 — se o usuário atual já marcou esta anotação como lida. */
  is_read_by_me?: boolean;
  /** v1.0.2 — última leitura registrada pelo usuário atual. */
  last_read_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface ContactNotesTableProps {
  items: ContactNoteRow[];
  currentUserId: string | null;
  isAdmin: boolean;
  deletingId: string | null;
  onRowClick: (row: ContactNoteRow) => void;
  onDelete: (row: ContactNoteRow) => void;
}

/** Trunca string preservando palavras (aprox.). */
function previewText(plain: string, max = 100): string {
  const t = (plain || '').trim();
  if (t.length <= max) return t || '(sem texto)';
  return t.slice(0, max).trimEnd() + '…';
}

export function ContactNotesTable({
  items,
  currentUserId,
  isAdmin,
  deletingId,
  onRowClick,
  onDelete,
}: ContactNotesTableProps) {
  return (
    <div className="border rounded-md overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Anotação</TableHead>
            <TableHead className="w-[64px] md:w-[110px]">Anexos</TableHead>
            <TableHead className="w-[56px] md:w-[90px]">Lida</TableHead>
            <TableHead className="w-[200px] hidden md:table-cell">Autor</TableHead>
            <TableHead className="w-[160px] hidden md:table-cell">
              Atualizado
            </TableHead>
            <TableHead className="w-[48px] md:w-[60px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row) => {
            const canDelete = isAdmin || row.created_by === currentUserId;
            return (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onRowClick(row)}
              >
                <TableCell className="min-w-[160px] md:max-w-[420px] align-top">
                  <span className="text-sm break-words line-clamp-3 md:line-clamp-none">{previewText(row.content_plain)}</span>
                </TableCell>
                <TableCell>
                  {row.attachments_count > 0 ? (
                    <Badge variant="secondary" className="gap-1">
                      <Paperclip className="h-3 w-3" />
                      {row.attachments_count}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                {/* Lida — CheckCheck verde quando o usuário atual já leu; contagem total ao lado */}
                <TableCell>
                  <div
                    className="flex items-center gap-1"
                    title={
                      row.is_read_by_me
                        ? `Você marcou como lida${row.read_count ? ` · ${row.read_count} no total` : ''}`
                        : row.read_count
                          ? `${row.read_count} ${row.read_count === 1 ? 'usuário leu' : 'usuários leram'}`
                          : 'Ainda não lida'
                    }
                  >
                    <CheckCheck
                      className={cn(
                        'h-4 w-4',
                        row.is_read_by_me
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-muted-foreground/40',
                      )}
                    />
                    {(row.read_count ?? 0) > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {row.read_count}
                      </span>
                    )}
                  </div>
                </TableCell>
                {/* Autor — avatar + nome (mesmo padrão de ScheduledMessagesTable) */}
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 min-w-0">
                    <SmartAvatar
                      size="xs"
                      src={row.created_by_avatar ?? undefined}
                      name={row.created_by_name ?? undefined}
                    />
                    <span className="truncate">{row.created_by_name || '—'}</span>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                  {format(new Date(row.updated_at), "dd/MM/yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-8 w-8 text-destructive hover:text-destructive',
                      !canDelete && 'invisible',
                    )}
                    disabled={!canDelete || deletingId === row.id}
                    onClick={() => onDelete(row)}
                    aria-label="Excluir anotação"
                  >
                    {deletingId === row.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
